import { AlertTriangle, Banknote, HandCoins, Landmark, Repeat } from "lucide-react"
import { useState } from "react"
import { Modal } from "@/componentes/ui/Modal"

import { formatearImporte } from "./PanelRubros"

/**
 * Modal de pago de un rubro, con sus caminos de excepción.
 *
 * ⚠ **No se cierra cuando el pago falla, y ésa es su decisión central.** Un modal
 * que se cierra ante un error obliga a volver a abrirlo y a teclearlo todo otra vez,
 * y lo que se pierde no es tiempo: es el contexto de por qué se estaba pagando eso.
 * El error se muestra **dentro**, con lo tecleado intacto y con las salidas que
 * existen — convertir divisa o financiar.
 *
 * ⚠ **Las tres vías no son un desplegable indiferente.** Confundir `FONDO_OPERATIVO`
 * con `FINANCIADO` es confundir de quién es el dinero, así que cada una se explica
 * en una línea al elegirla.
 *
 * ⚠ **Las vías que mueven banco piden la cuenta aquí, no después.** El servidor las
 * rechaza sin ella —`pagar_rubro()` lanza `CONFLICTO` con «La via X exige una cuenta
 * bancaria»— así que un modal sin este selector deja dos de las tres vías rotas de
 * forma permanente: el operador elige FINANCIADO, pulsa Pagar, y recibe un error
 * sobre un campo que el formulario nunca le ofreció. `CLIENTE` no lo pide porque no
 * mueve banco: el importador pagó directo.
 */

export type Via = "CLIENTE" | "FONDO_OPERATIVO" | "FINANCIADO"

/** Lo mínimo que hace falta para elegir de dónde sale el dinero. */
export type CuentaDePago = {
  id: number
  nombre: string
  banco: string
}

/** El sobre de error de §5, tal como lo entrega el backend. */
export type ErrorPago = {
  code: "MONTO_EXCEDE_SALDO" | "SALDO_INSUFICIENTE" | "TASA_NO_DISPONIBLE" | "CONFLICTO"
  message: string
}

const EXPLICACION: Record<Via, string> = {
  CLIENTE: "El cliente pagó directo. No mueve banco.",
  FONDO_OPERATIVO: "Sale del anticipo del cliente para este expediente.",
  FINANCIADO: "Sale de la agencia y genera una cuenta por cobrar al cliente.",
}

const ICONO: Record<Via, typeof Banknote> = {
  CLIENTE: HandCoins,
  FONDO_OPERATIVO: Banknote,
  FINANCIADO: Repeat,
}

/** Si esta vía mueve dinero de una cuenta de la agencia. El reflejo del servicio. */
export function exigeCuenta(via: Via): boolean {
  return via !== "CLIENTE"
}

type Props = {
  concepto: string
  pendiente: string
  moneda: "USD" | "BS"
  /** Cuentas entre las que elegir. Vacío es un estado real: aún no hay ninguna. */
  cuentas?: CuentaDePago[]
  error?: ErrorPago | null
  onPagar: (datos: { via: Via; monto: string; cuentaId: number | null }) => void
  onCerrar: () => void
}

export function ModalPago({
  concepto,
  pendiente,
  moneda,
  cuentas = [],
  error,
  onPagar,
  onCerrar,
}: Props) {
  const [via, setVia] = useState<Via>("CLIENTE")
  // Pre-llenado con lo pendiente, que es lo que se paga el 90 % de las veces.
  const [monto, setMonto] = useState(pendiente)
  const [cuentaId, setCuentaId] = useState<number | null>(cuentas[0]?.id ?? null)
  const Icono = ICONO[via]

  // Falta un dato que el servidor va a exigir. Se sabe aquí, así que se dice aquí.
  const faltaCuenta = exigeCuenta(via) && cuentaId === null

  return (
    // ⚠ **Un `Modal` de verdad (12/10/2026).** Era un panel dentro de la página que
    // se anunciaba `aria-modal` sin serlo: sin capa, sin Escape y sin foco, en una
    // pantalla de dinero. Escape y la X equivalen a cancelar.
    <Modal abierto titulo={`Pagar ${concepto}`} onCerrar={onCerrar} ancho="md">
      <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
        Pendiente: <span className="tabular-nums">{formatearImporte(pendiente, moneda, "")}</span>
      </p>

      <fieldset className="mt-3">
        <legend className="text-sm">Vía de pago</legend>
        {(["CLIENTE", "FONDO_OPERATIVO", "FINANCIADO"] as const).map((v) => (
          <label key={v} className="mt-1 flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="via"
              value={v}
              checked={via === v}
              onChange={() => setVia(v)}
            />
            <span>{v.replace("_", " ")}</span>
          </label>
        ))}
        <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          <Icono size={14} aria-hidden={true} />
          {EXPLICACION[via]}
        </p>
      </fieldset>

      {/*
        Sólo para las vías que mueven banco. Enseñárselo a `CLIENTE` sugeriría que el
        pago directo del importador sale de una cuenta de la agencia, que es
        exactamente lo contrario de lo que esa vía significa.
      */}
      {exigeCuenta(via) &&
        (cuentas.length === 0 ? (
          <p
            role="status"
            className="mt-3 flex items-center gap-2 rounded-[var(--radius-control)] border p-2 text-xs"
            style={{ borderColor: "var(--warning)", color: "var(--warning)" }}
          >
            <Landmark size={14} aria-hidden={true} />
            No hay ninguna cuenta bancaria disponible. Esta vía necesita una: revísalo en Tesorería,
            o paga por la vía CLIENTE.
          </p>
        ) : (
          <label className="mt-3 block text-sm">
            Cuenta de origen
            <select
              className="campo mt-1"
              value={cuentaId ?? ""}
              onChange={(e) => setCuentaId(Number(e.target.value))}
            >
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre} · {c.banco}
                </option>
              ))}
            </select>
          </label>
        ))}

      <label className="mt-3 block text-sm">
        Monto
        <input
          className="campo mt-1 text-right tabular-nums"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
        />
      </label>

      {/*
        ⚠ El error vive DENTRO del modal y no lo cierra. `role="alert"` para que un
        lector de pantalla lo anuncie sin que el foco salte: quien acaba de teclear
        un monto sigue en el campo del monto.
      */}
      {error && (
        <div
          role="alert"
          className="mt-3 flex gap-2 rounded-[var(--radius-control)] border p-2 text-sm"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertTriangle size={16} aria-hidden={true} />
          <span>
            <strong>{error.code}</strong> · {error.message}
          </span>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-[var(--radius-control)] border px-3 py-1.5"
          style={{ borderColor: "var(--border)" }}
        >
          Cancelar
        </button>
        {/*
          Deshabilitado sólo cuando falta la cuenta, y con el motivo escrito justo
          encima. La regla de `TablaSaldos` —un botón gris invita a pulsarlo y
          devuelve un error que no explica nada— se respeta porque aquí la
          explicación está a la vista y el remedio está en el mismo formulario.
        */}
        <button
          type="button"
          disabled={faltaCuenta}
          onClick={() => onPagar({ via, monto, cuentaId: exigeCuenta(via) ? cuentaId : null })}
          className="rounded-[var(--radius-control)] px-3 py-1.5"
          style={{
            background: "var(--primary)",
            color: "var(--primary-fg)",
            opacity: faltaCuenta ? 0.5 : 1,
          }}
        >
          Pagar
        </button>
      </div>
    </Modal>
  )
}
